import React, { useState } from 'react';
import { personalInfo } from '../mockData';
import { Mail, MapPin, Github, Linkedin, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    console.log('Form submitted:', formData);
    
    toast({
      title: "Message sent!",
      description: "Thanks for reaching out. I'll get back to you soon.",
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">Get in Touch</h1>
          <p className="text-xl text-neutral-400 max-w-3xl">
            Looking to collaborate on a project, discuss an opportunity, or just want to say hi? I'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-6 w-full max-w-xl mx-auto lg:mx-0">
              <div className="flex items-start gap-3 mb-4">
                <Mail className="text-cyan-400 mt-1 shrink-0" size={20} strokeWidth={2} />
                <div>
                  <h3 className="font-semibold text-white mb-1">Email</h3>
                  <a
                    href={`mailto:${personalInfo.email}`}
                    className="text-neutral-400 hover:text-cyan-400 transition-colors text-sm sm:text-base tracking-tight"
                  >
                    {personalInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3 mb-4">
                <Phone className="text-cyan-400 mt-1" size={20} />
                <div>
                  <h3 className="font-semibold text-white mb-1">Phone</h3>
                  <a
                    href={`tel:${personalInfo.phone}`}
                    className="text-neutral-400 hover:text-cyan-400 transition-colors text-sm sm:text-base tracking-tight"
                  >
                    {personalInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3 mb-4">
                <MapPin className="text-cyan-400 mt-1" size={20} />
                <div>
                  <h3 className="font-semibold text-white mb-1">Location</h3>
                  <p className="text-neutral-400">{personalInfo.location}</p>
                </div>
              </div>

              <div className="pt-4 border-t border-neutral-800">
                <h3 className="font-semibold text-white mb-3">Connect</h3>
                <div className="flex gap-3">
                  <a
                    href={personalInfo.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-10 h-10 bg-neutral-800 hover:bg-neutral-700 rounded-lg transition-colors"
                    aria-label="GitHub"
                  >
                    <Github size={18} />
                  </a>
                  <a
                    href={personalInfo.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-10 h-10 bg-neutral-800 hover:bg-neutral-700 rounded-lg transition-colors"
                    aria-label="LinkedIn"
                  >
                    <Linkedin size={18} />
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-6 w-full max-w-lg mx-auto lg:mx-0">
              <h3 className="font-semibold text-white mb-3">Response Time</h3>
              <p className="text-neutral-400 text-sm">
                I typically respond within 24-48 hours during weekdays.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-8">
              <div className="mb-6">
                <label htmlFor="name" className="block text-sm font-medium text-neutral-300 mb-2">
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-neutral-800 border-neutral-700 text-white focus:border-cyan-500"
                  placeholder="Your name"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-neutral-300 mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-neutral-800 border-neutral-700 text-white focus:border-cyan-500"
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-neutral-300 mb-2">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="bg-neutral-800 border-neutral-700 text-white focus:border-cyan-500 resize-none"
                  placeholder="Tell me about your project or what you'd like to discuss..."
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-medium py-3 rounded-lg transition-colors"
              >
                Send Message
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
